/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/24/22
 */

import React from "react";
import {Divider, FormControl, InputLabel, MenuItem, Select, Stack} from "@mui/material";
import {
	addDays,
	addMonths,
	addQuarters,
	addWeeks, addYears,
	startOfMonth,
	startOfQuarter,
	startOfToday,
	startOfWeek, startOfYear
} from "date-fns";
import DateRangePicker from "../components/DateRangePicker";
import {locale} from "../dates-helper";

export interface DateFilterControlProps {
	label?: string
	range?: Interval
	onChange: (range: Interval|null) => void
	showToday?: boolean
	showYesterday?: boolean
	showTomorrow?: boolean
	showThisIntervals?: boolean
	showLastIntervals?: boolean
	showNextIntervals?: boolean
	showCustom?: boolean
}

const ranges: Record<string, Interval> = {
	today: { start: startOfToday(), end: addDays(startOfToday(), 1) },
	yesterday: { start: addDays(startOfToday(), -1), end: startOfToday() },
	tw: {
		start: startOfWeek(new Date(), { locale }),
		end: addWeeks(startOfWeek(new Date(), { locale }), 1)
	},
	tm: { start: startOfMonth(new Date()), end: addMonths(startOfMonth(new Date()), 1) },
	tq: { start: startOfQuarter(new Date()), end: addQuarters(startOfQuarter(new Date()), 1) },
	ty: { start: startOfYear(new Date()), end: addYears(startOfYear(new Date()), 1) },
	tomorrow: { start: addDays(startOfToday(), 1), end: addDays(startOfToday(), 2) },
	nw: {
		start: addWeeks(startOfWeek(new Date(), { locale }), 1),
		end: addWeeks(startOfWeek(new Date(), { locale }), 2)
	},
	nm: { start: addMonths(startOfMonth(new Date()), 1), end: addMonths(startOfMonth(new Date()), 2) },
	nq: { start: addQuarters(startOfQuarter(new Date()), 1), end: addQuarters(startOfQuarter(new Date()), 2) },
	ny: { start: addYears(startOfYear(new Date()), 1), end: addYears(startOfYear(new Date()), 2) },
	lw: {
		start: addWeeks(startOfWeek(new Date(), { locale }), -1),
		end: startOfWeek(new Date(), { locale })
	},
	lm: { start: addMonths(startOfMonth(new Date()), -1), end: startOfMonth(new Date()) },
	lq: { start: addQuarters(startOfQuarter(new Date()), -1), end: startOfQuarter(new Date()) },
	ly: { start: addYears(startOfYear(new Date()), -1), end: startOfYear(new Date()) }
}

const findRange = (range?: Interval) => {
	if (range) {
		for (let k in ranges) {
			if (range?.start === ranges[k].start && range.end === ranges[k].end) {
				return k;
			}
		}
	}
	return undefined;
}

export default function DateFilterControl(props: DateFilterControlProps) {

	const [preset, setPreset] = React.useState(() => {
		if (props.range) {
			return findRange(props.range) ?? "custom";
		} else {
			return "";
		}
	});
	const [customStart, setCustomStart] = React.useState(() => {
		const range = findRange(props.range);
		return range ? ranges[range].start : startOfToday();
	});
	const [customEnd, setCustomEnd] = React.useState(() => {
		const range = findRange(props.range);
		return range ? ranges[range].end : addDays(startOfToday(), 1);
	});

	const onChange = (preset: string) => {
		setPreset(preset);
		props.onChange(preset ? ranges[preset] : null)
	}

	return (
		<Stack direction={"row"} spacing={1} alignItems={"flex-start"}>
			<FormControl sx={{ width: 160 }} variant={"standard"}>
				{props.label && <InputLabel>{props.label}</InputLabel>}
				<Select
					value={preset}
					onChange={(event) => onChange(event.target.value as string)}
				>
					<MenuItem value={""}>No filter</MenuItem>
					{(props.showToday ?? true) && <MenuItem value={"today"}>Today</MenuItem>}
					{(props.showThisIntervals ?? true) && [
						<Divider key={0} />,
						<MenuItem key={"tw"} value={"tw"}>This Week</MenuItem>,
						<MenuItem key={"tm"} value={"tm"}>This Month</MenuItem>,
						<MenuItem key={"tq"} value={"tq"}>This Quarter</MenuItem>,
						<MenuItem key={"ty"} value={"ty"}>This Year</MenuItem>,
					]}
					{(props.showLastIntervals ?? true) && [
						<Divider key={0} />,
						(props.showYesterday ?? true) && <MenuItem key={"yesterday"} value={"yesterday"}>Yesterday</MenuItem>,
						<MenuItem key={"lw"} value={"lw"}>Last Week</MenuItem>,
						<MenuItem key={"lm"} value={"lm"}>Last Month</MenuItem>,
						<MenuItem key={"lq"} value={"lq"}>Last Quarter</MenuItem>,
						<MenuItem key={"ly"} value={"ly"}>Last Year</MenuItem>,
					]}
					{(props.showNextIntervals ?? true) && [
						<Divider key={0} />,
						(props.showTomorrow ?? true) && <MenuItem key={"tomorrow"} value={"tomorrow"}>Tomorrow</MenuItem>,
						<MenuItem key={"nw"} value={"nw"}>Next Week</MenuItem>,
						<MenuItem key={"nm"} value={"nm"}>Next Month</MenuItem>,
						<MenuItem key={"nq"} value={"nq"}>Next Quarter</MenuItem>,
						<MenuItem key={"ny"} value={"ny"}>Next Year</MenuItem>,
					]}
					{(props.showCustom ?? true) && [
						<Divider key={0} />,
						<MenuItem key={"custom"} value={"custom"}>Custom</MenuItem>
					]}
				</Select>
			</FormControl>
			{preset === "custom" &&
				<DateRangePicker
					range={{ start: customStart, end: customEnd }}
					onChange={(range) => {
						if (range) {
							setCustomStart(new Date(range.start));
							setCustomEnd(new Date(range.end));
							props.onChange(range);
						}
					}}
				/>
			}
		</Stack>
	);

}
